import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { DevopsApplication, DevopsEnvironment } from "../../types/devops";
import { devopsService } from "../../api/services/devopsService";
import { devopsRoutes } from "../../utils/devopsRoutes";

interface MapAppsSectionProps {
  environment: DevopsEnvironment;
  applications: DevopsApplication[];
  canEdit: boolean;
  onSaved: (item: DevopsEnvironment) => void;
}

const MapAppsSection: React.FC<MapAppsSectionProps> = ({
  environment,
  applications,
  canEdit,
  onSaved,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(new Set((environment.applications ?? []).map((app) => app.uuid)));
    setError(null);
  }, [environment]);

  const toggle = (uuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const handleSave = async () => {
    const current = new Set((environment.applications ?? []).map((app) => app.uuid));
    const toAdd = [...selected].filter((id) => !current.has(id));
    const toRemove = [...current].filter((id) => !selected.has(id));
    setSaving(true);
    setError(null);
    try {
      let updated = environment;
      for (const applicationUuid of toAdd) {
        updated = await devopsService.linkEnvironmentApplication(environment.uuid, applicationUuid);
      }
      for (const applicationUuid of toRemove) {
        updated = await devopsService.unlinkEnvironmentApplication(
          environment.uuid,
          applicationUuid,
        );
      }
      onSaved(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update mappings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Mapped applications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Either workspace role can map applications into an environment.
      </Typography>
      {canEdit ? (
        <FormGroup>
          {applications.map((app) => (
            <FormControlLabel
              key={app.uuid}
              control={
                <Checkbox checked={selected.has(app.uuid)} onChange={() => toggle(app.uuid)} />
              }
              label={`${app.name} (${app.applicationKind})`}
            />
          ))}
        </FormGroup>
      ) : (
        <FormGroup>
          {(environment.applications ?? []).map((app) => (
            <Typography key={app.uuid} variant="body2" sx={{ py: 0.5 }}>
              <RouterLink to={devopsRoutes.applicationView(app.uuid)}>{app.name}</RouterLink>
            </Typography>
          ))}
        </FormGroup>
      )}
      {applications.length === 0 && (
        <Typography color="text.secondary">No applications exist yet.</Typography>
      )}
      {!canEdit && (environment.applications ?? []).length === 0 && applications.length > 0 && (
        <Typography color="text.secondary">No applications mapped.</Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {canEdit && (
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleSave} disabled={saving}>
          Save mappings
        </Button>
      )}
    </Paper>
  );
};

export default MapAppsSection;
